import canvasesApi from '@/api/canvases.api';
import nodesApi from '@/api/nodes.api';
import linksApi from '@/api/links.api';
import {
  EXPORT_FORMAT,
  EXPORT_VERSION,
  type StratMapExport,
} from '@/types/export.types';
import type { Canvas } from '@/types/canvas.types';

export class ImportError extends Error {}

/** Parse + validate the JSON text produced by `exportToJSON`. */
export function parseCanvasJSON(text: string): StratMapExport {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new ImportError('Fichier JSON invalide');
  }
  const obj = parsed as Partial<StratMapExport>;
  if (!obj || typeof obj !== 'object') throw new ImportError('Format inattendu');
  if (obj.format !== EXPORT_FORMAT) {
    throw new ImportError(`Format inconnu : ${String(obj.format)}`);
  }
  if (typeof obj.version !== 'number' || obj.version > EXPORT_VERSION) {
    throw new ImportError(
      `Version ${String(obj.version)} non supportée (max ${EXPORT_VERSION})`,
    );
  }
  if (!obj.canvas || typeof obj.canvas.name !== 'string') {
    throw new ImportError('Bloc canvas manquant ou invalide');
  }
  if (!Array.isArray(obj.nodes) || !Array.isArray(obj.links)) {
    throw new ImportError('Blocs nodes/links manquants');
  }
  return obj as StratMapExport;
}

/**
 * Import a parsed export into the given workspace as a NEW canvas.
 * Nodes are recreated with fresh server-side IDs; links are remapped to those IDs.
 */
export async function importCanvas(
  data: StratMapExport,
  workspaceId: string,
): Promise<Canvas> {
  const canvas = await canvasesApi.create({
    workspaceId,
    name: `${data.canvas.name} (import ${new Date().toLocaleDateString('fr-FR')})`,
    description: data.canvas.description,
  });

  // Create all nodes and track local → new id mapping
  const idMap = new Map<string, string>();
  for (const n of data.nodes) {
    const created = await nodesApi.create({
      canvasId: canvas.id,
      type: n.type,
      title: n.title,
      description: n.description,
      status: n.status,
      priority: n.priority,
      positionX: n.position.x,
      positionY: n.position.y,
      budgetPlanned: n.budgetPlanned ?? undefined,
      budgetSpent: n.budgetSpent ?? undefined,
      effortPlannedDays: n.effortPlannedDays ?? undefined,
      effortSpentDays: n.effortSpentDays ?? undefined,
      progress: n.progress ?? undefined,
    });
    idMap.set(n.localId, created.id);
  }

  // Create links using the new node ids
  for (const l of data.links) {
    const sourceId = idMap.get(l.sourceLocalId);
    const targetId = idMap.get(l.targetLocalId);
    if (!sourceId || !targetId) continue; // orphan link, skip silently
    await linksApi.create({
      canvasId: canvas.id,
      sourceNodeId: sourceId,
      targetNodeId: targetId,
      type: l.type,
      intensity: l.intensity,
      label: l.label,
      description: l.description,
    });
  }

  return canvas;
}
